// src/lib/voiceAudio.js
//
// Plain array work on a journal recording: no imports, no network, so
// scripts/check-voice-fallback.mjs can run every function here in Node.
//
// Why it exists. A journal recording can be up to 3 minutes
// (VoiceNoteRecorder's useVoiceRecorder({ maxDurationMs: 180_000 })). The
// only way to turn speech into text without storing the audio first is
// voice-to-text's sync recognize, which takes about a minute per request:
// Google's own cap, and the function's MAX_AUDIO_BYTES backstop
// (supabase/functions/voice-to-text/index.ts). So when storing the
// recording fails, or transcribing the stored copy fails, the recording that
// is still in memory is cut into pieces short enough for sync recognize and
// each piece is transcribed on its own (src/lib/voiceTranscribeInline.js).
// The reader's words survive either failure.
//
// resample() and encodeWav16() repeat two small functions that already live
// in src/hooks/useVoiceRecorder.js. On purpose: that file is mirrored byte
// for byte with reanlo-author (scripts/mirrored.json), so exporting them
// from it would mean changing both repos for a reader-only feature.

// What sync recognize is fed. 16kHz is what Google downsamples to anyway
// (useVoiceRecorder.js, TRANSCRIBE_SAMPLE_RATE_HZ, same value).
export const SYNC_RECOGNIZE_RATE_HZ = 16000;

// The longest piece sent in one request. Must stay under BOTH of
// voice-to-text's limits: Google's ~60s sync-recognize cap, and
// MAX_AUDIO_BYTES (9MB decoded, voice-to-text/index.ts). 50s at 16kHz mono
// 16-bit is 1.6MB, so the 60s cap is the one that binds, with 10s to spare.
export const PIECE_MAX_SECONDS = 50;

// Where a cut is allowed to land: within the last PIECE_SEARCH_SECONDS of a
// piece, at the quietest PIECE_WINDOW_MS stretch found there. Cutting in a
// pause rather than at exactly 50.000s keeps a word from being split across
// two requests, where neither half would be recognised.
export const PIECE_SEARCH_SECONDS = 5;
export const PIECE_WINDOW_MS = 100;

// Reads the mono 16-bit PCM WAV that useVoiceRecorder's encodeWav writes
// (and only that: anything else throws, rather than being misread as
// noise). Walks the chunk list instead of assuming a 44-byte header, so a
// file with an extra chunk before 'data' still parses.
export function parseWav(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  const tag = (offset) => String.fromCharCode(
    view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3),
  );
  if (arrayBuffer.byteLength < 12 || tag(0) !== 'RIFF' || tag(8) !== 'WAVE') {
    throw new Error('Not a WAV file.');
  }

  let offset = 12;
  let sampleRate = null;
  let format = null;
  while (offset + 8 <= arrayBuffer.byteLength) {
    const id = tag(offset);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      format = {
        audioFormat: view.getUint16(body, true),
        channels: view.getUint16(body + 2, true),
        bitsPerSample: view.getUint16(body + 14, true),
      };
      sampleRate = view.getUint32(body + 4, true);
    } else if (id === 'data') {
      if (!format) throw new Error('WAV data before its format chunk.');
      if (format.audioFormat !== 1 || format.channels !== 1 || format.bitsPerSample !== 16) {
        throw new Error(`Unsupported WAV: format ${format.audioFormat}, ${format.channels} channel(s), ${format.bitsPerSample}-bit.`);
      }
      const count = Math.floor(Math.min(size, arrayBuffer.byteLength - body) / 2);
      const samples = new Float32Array(count);
      for (let i = 0; i < count; i++) samples[i] = view.getInt16(body + i * 2, true) / 0x8000;
      return { samples, sampleRate };
    }
    offset = body + size + (size % 2); // chunks are word-aligned
  }
  throw new Error('WAV file has no data chunk.');
}

// Linear interpolation, same as useVoiceRecorder.js's resampleFloat32.
export function resample(input, fromRate, toRate) {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = fromRate / toRate;
  const newLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const srcFloor = Math.floor(srcIndex);
    const srcCeil = Math.min(srcFloor + 1, input.length - 1);
    const frac = srcIndex - srcFloor;
    output[i] = input[srcFloor] * (1 - frac) + input[srcCeil] * frac;
  }
  return output;
}

// Splits a recording into [start, end) sample ranges, each at most
// maxSeconds long, together covering every sample exactly once, in order.
// A recording no longer than maxSeconds comes back as one range.
export function splitIntoPieces(samples, sampleRate, {
  maxSeconds = PIECE_MAX_SECONDS,
  searchSeconds = PIECE_SEARCH_SECONDS,
  windowMs = PIECE_WINDOW_MS,
} = {}) {
  const maxLen = Math.floor(maxSeconds * sampleRate);
  const searchLen = Math.min(Math.floor(searchSeconds * sampleRate), maxLen - 1);
  const windowLen = Math.max(1, Math.floor((windowMs / 1000) * sampleRate));
  const pieces = [];
  let start = 0;
  while (start < samples.length) {
    if (samples.length - start <= maxLen) {
      pieces.push([start, samples.length]);
      break;
    }
    const limit = start + maxLen;
    let cut = limit;
    let quietest = Infinity;
    for (let w = limit - searchLen; w + windowLen <= limit; w += Math.max(1, Math.floor(windowLen / 2))) {
      let sum = 0;
      for (let i = w; i < w + windowLen; i++) sum += samples[i] * samples[i];
      if (sum < quietest) {
        quietest = sum;
        cut = w + Math.floor(windowLen / 2);
      }
    }
    pieces.push([start, cut]);
    start = cut;
  }
  return pieces;
}

// Mono 16-bit PCM WAV bytes, same layout as useVoiceRecorder.js's
// encodeWav (which returns a Blob; bytes here so Node can check them).
export function encodeWav16(samples, sampleRate) {
  const dataSize = samples.length * 2;
  const bytes = new Uint8Array(44 + dataSize);
  const view = new DataView(bytes.buffer);
  const writeTag = (offset, s) => { for (let i = 0; i < 4; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
  writeTag(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeTag(8, 'WAVE');
  writeTag(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeTag(36, 'data');
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return bytes;
}

// Base64 without FileReader, in 32KB slices: spreading a whole recording
// into one String.fromCharCode call overflows the call stack.
export function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
